import { StandardEvents } from '@shopify/events';
import { QuantitySelectorUpdateEvent } from '@theme/events';

export function buildPair(first, second) {
  if (!first?.available || !second?.available) return null;
  const items = first.id === second.id
    ? [{ variantId: first.id, quantity: 2 }]
    : [{ variantId: first.id, quantity: 1 }, { variantId: second.id, quantity: 1 }];
  for (const line of items) {
    const v = line.variantId === first.id ? first : second;
    if (line.quantity < v.min || (v.max !== null && line.quantity > v.max) || (line.quantity - v.min) % v.increment !== 0) return null;
  }
  return items;
}
export function offerTotal(firstPrice, secondPrice, quantity, active, currency) {
  if (quantity === 1) return firstPrice;
  const discount = active && currency === 'MXN' ? 200 : 0;
  return Math.max(0, firstPrice - discount) + Math.max(0, secondPrice - discount);
}
class AlinevaOffers extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    const { signal } = this.abort;
    this.section = this.closest('.shopify-section');
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.quantity = 1;
    this.addEventListener('change', () => { this.quantity = Number(this.querySelector('input:checked')?.value || 1); this.refresh(); }, { signal });
    this.section.addEventListener('alineva:prepare-cart', e => this.prepare(e), { signal });
    this.section.addEventListener(StandardEvents.productSelect, e => {
      const pending = e.promise; this.pendingVariant = pending;
      pending.then(() => requestAnimationFrame(() => {
        if (this.pendingVariant === pending) this.pendingVariant = null;
        if (this.isConnected) this.refresh();
      })).catch(() => { if (this.pendingVariant === pending) this.pendingVariant = null; });
    }, { signal });
    this.section.addEventListener('submit', e => {
      if (this.quantity === 2 && this.pendingVariant) {
        e.preventDefault(); e.stopImmediatePropagation();
        const error = this.querySelector('.alineva-offer-error');
        error.textContent = this.dataset.error; error.hidden = false;
      }
    }, { capture: true, signal });
    // Theme's variant components update in promise callbacks; refresh when the native ID changes.
    this.observer = new MutationObserver(() => this.refresh());
    const form = this.section.querySelector('product-form-component form');
    if (form) this.observer.observe(form, { subtree: true, attributes: true, attributeFilter: ['value'] });
    requestAnimationFrame(() => this.refresh());
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
  selected(id) { return this.variants.find(v => v.id === String(id)); }
  first() { return this.selected(this.section.querySelector('product-form-component input[name="id"]')?.value); }
  second() { return this.selected(this.querySelector('[data-second]').value); }
  format(value) { return new Intl.NumberFormat(document.documentElement.lang || 'es-MX', { style: 'currency', currency: this.dataset.currency, currencyDisplay: 'code' }).format(value / 100); }
  refresh() {
    const first = this.first(); if (!first) return;
    const second = this.second() || first;
    const active = this.dataset.active === 'true' && this.dataset.currency === 'MXN';
    this.section.classList.toggle('alineva-pair', this.quantity === 2);
    this.querySelector('[data-second-wrap]').hidden = this.quantity !== 2;
    this.querySelector('[data-total]').textContent = this.format(offerTotal(first.price, second.price, this.quantity, active, this.dataset.currency));
    this.querySelectorAll('[data-card-price]').forEach(el => { el.textContent = this.format(offerTotal(first.price, second.price, Number(el.dataset.cardPrice), active, this.dataset.currency)); });
    const shipping = this.querySelector('[data-shipping]'); shipping.textContent = this.quantity === 2 && active ? shipping.dataset.free : shipping.dataset.normal;
    const input = this.section.querySelector('product-form-component input[name="quantity"]');
    // Batch validation uses individual quantities. Native selector remains at one for different variants.
    const nativeQuantity = this.quantity === 2 && first.id === second.id ? 2 : Math.max(1, first.min);
    if (input && Number(input.value) !== nativeQuantity) { input.value = nativeQuantity; input.dispatchEvent(new Event('change', { bubbles: true })); }
    const error = this.querySelector('.alineva-offer-error');
    error.hidden = this.quantity !== 2 || Boolean(buildPair(first, second));
    error.textContent = this.dataset.error;
    document.querySelectorAll('[data-story-price], [data-story-unit]').forEach(el => {
      const q = Number(el.dataset.storyPrice || el.dataset.storyUnit);
      const total = offerTotal(first.price, second.price, q, el.dataset.active === 'true', this.dataset.currency);
      el.textContent = this.format(el.hasAttribute('data-story-unit') ? total / q : total);
    });
    const sticky = this.section.querySelector('.sticky-add-to-cart__price');
    if (sticky) sticky.textContent = this.querySelector('[data-total]').textContent;
    this.section.dispatchEvent(new QuantitySelectorUpdateEvent(this.quantity));
  }
  choose(q) {
    const radio = this.querySelector(`input[type="radio"][value="${q}"]`);
    if (!radio) return;
    radio.checked = true; this.quantity = q; this.refresh();
    this.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
    radio.focus({ preventScroll: true });
  }
  prepare(event) {
    if (this.quantity !== 2) return;
    const first = this.selected(event.detail.variantId), second = this.second();
    const items = buildPair(first, second);
    const form = this.section.querySelector('product-form-component form');
    // Native batch flow does not carry subscription plans or line-item properties.
    if (!items || new FormData(form).has('selling_plan') || [...new FormData(form).keys()].some(k => k.startsWith('properties['))) {
      event.preventDefault(); const error = this.querySelector('.alineva-offer-error'); error.textContent = this.dataset.error; error.hidden = false; this.scrollIntoView({ block: 'center' }); return;
    }
    event.detail.items = items;
  }
}
if (!customElements.get('alineva-offers')) customElements.define('alineva-offers', AlinevaOffers);
document.addEventListener('click', e => {
  const button = e.target.closest('[data-alineva-choose]');
  if (!button) return;
  const offers = document.querySelector('alineva-offers');
  if (offers) { e.preventDefault(); offers.choose(Number(button.dataset.alinevaChoose)); }
});
